import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import LoginPage from './components/LoginPage';
import EmployeeDashboard from './components/EmployeeDashboard';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import './index.css';

/* ─────────────────────────────────────────────────────────────────────────────
   LoginTransitionOverlay
   Phases:
     covering  (0–600ms)  — black overlay sweeps in over the login page
     holding   (600–1200ms) — logo + welcome greeting displayed on black
     revealing (1200–1700ms) — overlay fades out, dashboard already mounted underneath
   ───────────────────────────────────────────────────────────────────────────── */
const KAIRALI_LOGO = 'https://www.kairali.com/images/kairali-logo.png';

const transitionCSS = `
  @keyframes lt-cover-in {
    0%   { clip-path: circle(0%   at 50% 60%); opacity: 1; }
    100% { clip-path: circle(150% at 50% 60%); opacity: 1; }
  }
  @keyframes lt-fade-out {
    0%   { opacity: 1; }
    100% { opacity: 0; }
  }
  @keyframes lt-logo-in {
    0%   { opacity: 0; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes lt-greet-in {
    0%   { opacity: 0; transform: translateY(14px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes lt-bar-fill {
    0%   { width: 0%; }
    100% { width: 100%; }
  }
  @keyframes lt-logo-pulse {
    0%,100% { box-shadow: 0 0 0   0   rgba(78,189,116,0); }
    50%     { box-shadow: 0 0 40px 12px rgba(78,189,116,0.30); }
  }
  @keyframes dash-enter {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .lt-covering {
    animation: lt-cover-in 0.55s cubic-bezier(0.76, 0, 0.24, 1) both;
  }
  .lt-revealing {
    animation: lt-fade-out 0.5s ease both;
  }
  .lt-logo-ring {
    animation: lt-logo-pulse 1.8s ease-in-out infinite, lt-logo-in 0.4s ease both;
  }
  .lt-greet    { animation: lt-greet-in 0.4s ease both 0.1s; }
  .lt-name     { animation: lt-greet-in 0.4s ease both 0.22s; }
  .lt-bar-fill { animation: lt-bar-fill 0.55s ease both 0.15s; }
  .dashboard-enter { animation: dash-enter 0.55s ease both 0.1s; }
`;

function LoginTransitionOverlay({ phase, user }) {
  const showContent = phase === 'holding' || phase === 'revealing';

  return (
    <div
      className={phase === 'revealing' ? 'lt-revealing' : 'lt-covering'}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'all',
      }}
    >
      {/* Radial green glow — bottom left */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 65% 50% at 10% 90%, rgba(12,40,22,0.90) 0%, transparent 70%)',
      }} />
      {/* Dot grid */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />

      {/* Welcome content — only in holding/revealing */}
      {showContent && (
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 32px' }}>

          {/* Logo ring */}
          <div
            className="lt-logo-ring"
            style={{
              width: 88, height: 88, borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(78,189,116,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <div style={{
              width: 70, height: 70, borderRadius: '50%',
              background: 'rgba(255,255,255,0.96)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              <img
                src={KAIRALI_LOGO}
                alt="Kairali"
                style={{ width: 52, height: 'auto' }}
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.parentNode.style.background = 'linear-gradient(135deg,#2D6A46,#C8922A)';
                }}
              />
            </div>
          </div>

          {/* Greeting */}
          <p
            className="lt-greet"
            style={{
              fontSize: 12, fontWeight: 500, letterSpacing: '0.16em',
              color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase',
              marginBottom: 10, fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Welcome back
          </p>

          {/* Name */}
          <h2
            className="lt-name"
            style={{
              fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.2,
              color: '#fff', marginBottom: 6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {user?.name || ''}
          </h2>

          {/* Role · Department */}
          <p
            className="lt-greet"
            style={{
              fontSize: 12, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.04em',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              animationDelay: '0.3s',
            }}
          >
            {[user?.role, user?.department].filter(Boolean).join(' · ')}
          </p>

          {/* Gold progress bar */}
          <div style={{
            width: 180, height: 2, background: 'rgba(255,255,255,0.08)',
            borderRadius: 2, margin: '28px auto 0', overflow: 'hidden',
          }}>
            <div
              className="lt-bar-fill"
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #D4A840, #E2BC56)',
                borderRadius: 2,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   AppContent
   ───────────────────────────────────────────────────────────────────────────── */
function AppContent() {
  const [currentUser, setCurrentUser] = useState(null);
  const [transitionPhase, setTransitionPhase] = useState('idle'); // idle | covering | holding | revealing
  const [pendingUser, setPendingUser] = useState(null);
  const { isDarkMode } = useContext(ThemeContext);
  const timerRef = useRef([]);

  const clearTimers = () => { timerRef.current.forEach(clearTimeout); timerRef.current = []; };
  const after = (ms, fn) => { const t = setTimeout(fn, ms); timerRef.current.push(t); };

  const handleLogout = useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('loginTime');
  }, []);

  const handleLogin = (user) => {
    const loginTime = Date.now();
    const { passkey: _passkey, ...safeUser } = user;
    setPendingUser({ user: safeUser, loginTime });
    setTransitionPhase('covering');
  };

  // Drive the phase sequence
  useEffect(() => {
    if (transitionPhase === 'covering') {
      // cover anim takes 550ms → switch to holding
      after(560, () => setTransitionPhase('holding'));
    } else if (transitionPhase === 'holding') {
      // hold welcome screen for 700ms, then mount dashboard + start revealing
      after(700, () => {
        if (pendingUser) {
          localStorage.setItem('currentUser', JSON.stringify(pendingUser.user));
          localStorage.setItem('loginTime', String(pendingUser.loginTime));
          setCurrentUser(pendingUser.user);
        }
        setTransitionPhase('revealing');
      });
    } else if (transitionPhase === 'revealing') {
      // reveal anim takes 500ms → done
      after(520, () => {
        setTransitionPhase('idle');
        setPendingUser(null);
      });
    }
    return clearTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionPhase]);

  // Restore session on load
  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    const savedLoginTime = localStorage.getItem('loginTime');
    if (savedUser && savedLoginTime) {
      const elapsed = Date.now() - parseInt(savedLoginTime, 10);
      if (elapsed < 10 * 60 * 60 * 1000) {
        setCurrentUser(JSON.parse(savedUser));
      } else {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('loginTime');
      }
    }
  }, []);

  // Auto-logout timer
  useEffect(() => {
    if (!currentUser) return;
    const saved = localStorage.getItem('loginTime');
    if (!saved) return;
    const remaining = 10 * 60 * 60 * 1000 - (Date.now() - parseInt(saved, 10));
    if (remaining <= 0) { handleLogout(); return; }
    const t = setTimeout(handleLogout, remaining);
    return () => clearTimeout(t);
  }, [currentUser, handleLogout]);

  // Dark mode class
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const loginTime = localStorage.getItem('loginTime');

  return (
    <>
      <style>{transitionCSS}</style>
      <div className="App">
        {currentUser ? (
          <div className={transitionPhase === 'revealing' ? 'dashboard-enter' : ''}>
            <EmployeeDashboard
              currentUser={currentUser}
              onLogout={handleLogout}
              loginTime={loginTime}
            />
          </div>
        ) : (
          <LoginPage onLogin={handleLogin} />
        )}
      </div>

      {transitionPhase !== 'idle' && (
        <LoginTransitionOverlay phase={transitionPhase} user={pendingUser?.user} />
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
